import _LessonPlayerFinishMutation from "../__generated__/LessonPlayerFinishMutation.graphql";
import _LessonPlayerStartMutation from "../__generated__/LessonPlayerStartMutation.graphql";
import _LessonPlayerQuery from "../__generated__/LessonPlayerQuery.graphql";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { Pause, Play, RotateCcw } from 'lucide-react';
import { En } from '../i18n/En';
import { t } from '../i18n/t';
import { localDay } from '../lessons/localDay.ts';
import { addInterval, heardFraction, finishRetryDelayMs, followScrollTop, follows, isComplete, lineAt, nextSpeed, seekBack, type Interval, type Speed } from '../lessons/player.ts';
import { registerFor } from '../profile/register';
import { LessonAnswer } from './LessonAnswer';
import { kindLabel } from './lessonLabels.ts';
import type { LessonPlayerQuery as LessonPlayerQueryType } from '../__generated__/LessonPlayerQuery.graphql';
import type { LessonPlayerFinishMutation } from '../__generated__/LessonPlayerFinishMutation.graphql';
import type { LessonPlayerStartMutation } from '../__generated__/LessonPlayerStartMutation.graphql';
const LessonPlayerQuery = (_LessonPlayerQuery.hash && _LessonPlayerQuery.hash !== "50d5e82726337fd4c1cea3403b6b73c7" && console.error("The definition of 'LessonPlayerQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _LessonPlayerQuery);
const StartPlay = (_LessonPlayerStartMutation.hash && _LessonPlayerStartMutation.hash !== "2875959581fd06ea2b08d6e776ae0e13" && console.error("The definition of 'LessonPlayerStartMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _LessonPlayerStartMutation);
const FinishPlay = (_LessonPlayerFinishMutation.hash && _LessonPlayerFinishMutation.hash !== "98383daf18da0e8d85ebe3933170f0fa" && console.error("The definition of 'LessonPlayerFinishMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _LessonPlayerFinishMutation);

/** A heard span longer than this between two `timeupdate`s was a seek, not listening. */
const MAX_STEP_MS = 1500;
function fmt(ms: number): string {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * The plain player (lesson library PRD §4.3) and the article's page. A play is
 * opened on the first press of play, and finished once 90 % of the recording's
 * distinct time has been heard. The transcript starts hidden every time and is
 * offered at every level; it is withheld only until the transcriber has it.
 * Shown, it is the only thing on the page that scrolls, so the controls stay
 * on screen while it follows the line being spoken; a line pressed is a seek.
 * The audio article offers its set's article in the same place instead.
 * The mini-story's Answer mode (`?mode=answer`, only when the server says the
 * story is `answerReady`) is `LessonAnswer`, under this page's title.
 */
export function LessonPlayer() {
  const {
    lessonId = ''
  } = useParams();
  const data = useLazyLoadQuery<LessonPlayerQueryType>(LessonPlayerQuery, {
    id: lessonId
  }, {
    fetchPolicy: 'network-only'
  });
  const [startPlay] = useMutation<LessonPlayerStartMutation>(StartPlay);
  const [finishPlay] = useMutation<LessonPlayerFinishMutation>(FinishPlay);
  const audio = useRef<HTMLAudioElement>(null);
  const heard = useRef<Interval[]>([]);
  const last = useRef<number | null>(null);
  const playId = useRef<string | null>(null);
  const opening = useRef(false);
  const finished = useRef(false);
  const finishFailures = useRef(0);
  const finishTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [playing, setPlaying] = useState(false);
  const [now, setNow] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState<Speed>(1.0);
  // One reading view at a time: both fill the same space under the controls.
  const [view, setView] = useState<'none' | 'transcript' | 'article'>('none');
  const articleRead = useRef(false);
  const [params] = useSearchParams();

  // `node` is any Node; Relay types an inline fragment's fields as optional.
  const n = data.node;
  const lesson = n?.id && n.kind && n.fileUrl !== undefined && n.lines ? {
    id: n.id,
    kind: n.kind,
    part: n.part,
    durationMs: n.durationMs,
    fileUrl: n.fileUrl,
    transcriptStatus: n.transcriptStatus,
    answerReady: n.answerReady ?? false,
    cues: n.cues ?? [],
    medians: n.answerMedians ?? [],
    lines: n.lines,
    // Read beside the audio article only, the recording of the same text.
    article: n.kind === 'AUDIO_ARTICLE' ? n.article ?? null : null
  } : null;
  const lessonKey = lesson?.id;
  const current = view === 'transcript' && lesson ? lineAt(lesson.lines, now) : null;

  // `scrollTo` on the pane rather than `scrollIntoView` on the line, which
  // scrolls every scrolling ancestor too: `main` included, taking the
  // controls off the top. `useLayoutEffect` for `SessionView`'s reason, so the
  // highlight and the scroll land in one frame.
  const pane = useRef<HTMLUListElement>(null);
  const scrolledAt = useRef<number | null>(null);
  useLayoutEffect(() => {
    const el = pane.current;
    const line = current === null ? null : el?.children[current];
    if (!el || !(line instanceof HTMLElement) || !follows(Date.now(), scrolledAt.current)) return;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollTo({
      top: followScrollTop({
        top: line.offsetTop,
        height: line.offsetHeight
      }, el),
      behavior: still ? 'auto' : 'smooth'
    });
  }, [current]);
  const open = useCallback((id: string, then?: (playId: string) => void) => {
    if (playId.current || opening.current) return;
    opening.current = true;
    startPlay({
      variables: {
        input: {
          lessonId: id,
          localDay: localDay(new Date()),
          mode: 'LISTEN'
        }
      },
      onCompleted: r => {
        playId.current = r.startLessonPlay.id;
        opening.current = false;
        then?.(r.startLessonPlay.id);
      },
      onError: () => {
        opening.current = false;
      }
    });
  }, [startPlay]);
  const finish = useCallback(() => {
    if (finished.current || !playId.current) return;
    finished.current = true;
    finishPlay({
      variables: {
        input: {
          lessonPlayId: playId.current
        }
      },
      onError: () => {
        // Held shut for a few seconds: this check runs on every `timeupdate`
        // past 90 %, and a server that keeps refusing was asked ~4 times a
        // second. It gives up after three tries.
        const delay = finishRetryDelayMs(++finishFailures.current);
        if (delay !== null) finishTimer.current = setTimeout(() => finished.current = false, delay);
      }
    });
  }, [finishPlay]);

  // The next press of play after hearing it through is a new play.
  const reset = () => {
    heard.current = [];
    last.current = null;
    playId.current = null;
    finished.current = false;
    finishFailures.current = 0;
  };
  useEffect(() => () => {
    if (finishTimer.current) clearTimeout(finishTimer.current);
  }, []);
  useEffect(() => {
    if (audio.current) audio.current.playbackRate = speed;
  }, [speed]);
  if (registerFor(data.me?.learner?.defaultCharacter?.audience) === 'kid') return <Navigate to="/books" replace />;
  if (!lesson || !lessonKey) {
    return <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 p-4">
        <p>{t('lessons-gone')}</p>
        <Link to="/lessons" className="link">
          {t('lessons-back')}
        </Link>
      </div>;
  }
  const title = <h1 className="text-2xl font-bold">
      {t(kindLabel(lesson.kind))}
      {lesson.part && <> {lesson.part}</>}
    </h1>;
  if (lesson.kind === 'ARTICLE') {
    return <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 p-4">
        <Link to="/lessons" className="link min-h-(--hit-min)">
          {t('lessons-back')}
        </Link>
        {title}
        {/* Opening it is the reading: a PDF has no duration to hear through,
            so the click records and finishes a play, best effort. */}
        <a className="btn btn-primary" href={lesson.fileUrl} target="_blank" rel="noreferrer" onClick={() => open(lessonKey, id => finishPlay({
        variables: {
          input: {
            lessonPlayId: id
          }
        },
        onCompleted: reset,
        onError: reset
      }))}>
          {t('lessons-open-article')}
        </a>
      </div>;
  }
  if (params.get('mode') === 'answer' && lesson.answerReady && lesson.cues.length > 0) {
    return <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 p-4">
        <Link to="/lessons" className="link min-h-(--hit-min)">
          {t('lessons-back')}
        </Link>
        {title}
        <LessonAnswer lessonId={lessonKey} fileUrl={lesson.fileUrl} durationMs={lesson.durationMs} cues={lesson.cues} medians={lesson.medians} />
      </div>;
  }
  const durationMs = lesson.durationMs ?? duration;
  const lines = lesson.lines;
  const canShowText = lesson.transcriptStatus === 'READY' && lines.length > 0;
  const article = lesson.article;

  // Opening the article here is reading it, as on its own page: a play opened
  // and finished at once, best effort, so the routine's article step is done
  // without a second visit. Once per visit, however often it is toggled.
  const readArticle = (id: string) => {
    if (articleRead.current) return;
    articleRead.current = true;
    startPlay({
      variables: {
        input: {
          lessonId: id,
          localDay: localDay(new Date()),
          mode: 'LISTEN'
        }
      },
      onCompleted: r => {
        finishPlay({
          variables: {
            input: {
              lessonPlayId: r.startLessonPlay.id
            }
          }
        });
      },
      onError: () => {
        articleRead.current = false;
      }
    });
  };
  // Input events rather than `scroll`, which the follow's own `scrollTo`
  // fires as well and would read as the learner's.
  const scrolledByHand = () => scrolledAt.current = Date.now();
  const sync = () => {
    const el = audio.current;
    if (!el) return;
    const ms = el.currentTime * 1000;
    setNow(ms);
    if (!el.paused && last.current !== null && ms - last.current > 0 && ms - last.current < MAX_STEP_MS) {
      heard.current = addInterval(heard.current, last.current, ms);
      if (isComplete(heardFraction(heard.current, durationMs))) finish();
    }
    last.current = ms;
  };
  const toggle = () => {
    const el = audio.current;
    if (!el) return;
    if (el.paused) {
      open(lessonKey);
      void el.play();
    } else el.pause();
  };

  // `h-full min-h-0`: the page fills `AppLayout`'s `main` exactly instead of
  // overflowing it, so a long transcript scrolls in its own pane.
  return <div className="mx-auto flex h-full min-h-0 w-full max-w-2xl flex-col gap-4 p-4">
      <Link to="/lessons" className="link min-h-(--hit-min)">
        {t('lessons-back')}
      </Link>
      {title}
      <audio ref={audio} src={lesson.fileUrl} preload="metadata" onPlay={() => {
      setPlaying(true);
      last.current = (audio.current?.currentTime ?? 0) * 1000;
    }} onPause={() => setPlaying(false)} onSeeking={() => last.current = null} onSeeked={() => last.current = (audio.current?.currentTime ?? 0) * 1000} onTimeUpdate={sync} onLoadedMetadata={() => setDuration((audio.current?.duration ?? 0) * 1000)} onEnded={() => {
      setPlaying(false);
      sync();
      reset();
    }} />
      <div className="flex items-center gap-3">
        <button className="btn btn-primary btn-circle btn-lg" aria-label={playing ? t('lessons-pause') : t('lessons-play')} onClick={toggle}>
          {playing ? <Pause aria-hidden /> : <Play aria-hidden />}
        </button>
        <button className="btn btn-ghost" aria-label={t('lessons-back-15')} onClick={() => {
        if (audio.current) audio.current.currentTime = seekBack(audio.current.currentTime * 1000) / 1000;
      }}>
          <RotateCcw aria-hidden className="size-5" />
          <span aria-hidden>15</span>
        </button>
        <button className="btn btn-outline btn-sm ms-auto min-h-(--hit-min)" onClick={() => setSpeed(s => nextSpeed(s))}>
          {t('lessons-speed', {
          speed: speed === 1.0 ? '1.0' : '0.85'
        })}
        </button>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <span className="tabular-nums">{fmt(now)}</span>
        <input type="range" className="range range-primary range-sm flex-1" aria-label={t('lessons-scrub')} min={0} max={Math.max(1, Math.round(durationMs))} value={Math.min(Math.round(now), Math.max(1, Math.round(durationMs)))} onChange={e => {
        if (audio.current) audio.current.currentTime = Number(e.target.value) / 1000;
      }} />
        <span className="tabular-nums">{fmt(durationMs)}</span>
      </div>
      {(canShowText || article) && <div className="flex flex-wrap items-center gap-x-6">
          {canShowText && <label className="flex min-h-(--hit-min) items-center gap-2">
              <input type="checkbox" className="toggle" checked={view === 'transcript'} onChange={e => setView(e.target.checked ? 'transcript' : 'none')} />
              {t('lessons-transcript')}
            </label>}
          {article && <label className="flex min-h-(--hit-min) items-center gap-2">
              <input type="checkbox" className="toggle" checked={view === 'article'} onChange={e => {
          setView(e.target.checked ? 'article' : 'none');
          if (e.target.checked) readArticle(article.id);
        }} />
              {t('lessons-article-toggle')}
            </label>}
        </div>}
      {canShowText && view === 'transcript' &&
    // `relative` so a line's `offsetTop` is measured from this pane.
    <ul ref={pane} onWheel={scrolledByHand} onTouchMove={scrolledByHand} onPointerDown={scrolledByHand} onKeyDown={scrolledByHand} className="rounded-box bg-base-200 relative -mt-2 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overscroll-contain p-2">
          {lines.map((l, i) => <li key={l.startMs}>
              <button type="button" aria-current={i === current ? 'true' : undefined} className={`min-h-(--hit-min) w-full rounded px-2 py-1 text-start leading-relaxed transition-colors ${i === current ? 'bg-primary/15 font-semibold' : current !== null && i < current ? 'text-base-content/60 hover:bg-base-300' : 'hover:bg-base-300'}`} onClick={() => {
          if (!audio.current) return;
          audio.current.currentTime = l.startMs / 1000;
          setNow(l.startMs);
          scrolledAt.current = null;
        }}>
                <En>{l.text}</En>
              </button>
            </li>)}
        </ul>}
      {article && view === 'article' &&
    // The browser's own PDF viewer, fitted to the width. iOS Safari draws
    // only the first page of a framed PDF, hence the way out to a tab.
    <div className="-mt-2 flex min-h-0 flex-1 flex-col gap-1">
          <iframe src={`${article.fileUrl}#view=FitH`} title={t('lessons-article-frame')} className="rounded-box bg-base-200 min-h-0 w-full flex-1" />
          <a className="link min-h-(--hit-min) self-start text-sm" href={article.fileUrl} target="_blank" rel="noreferrer">
            {t('lessons-open-article')}
          </a>
        </div>}
    </div>;
}